import { createContext, useCallback, useContext, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Sparkles } from "lucide-react";

// Shared "AI Planning Mode" entry transition. Every AI entry point (bottom-nav
// pill, hero CTA, Shop/ProductDetail/Checkout/Occasion/Experience CTAs) calls
// playAI(callback, event) instead of navigating directly. The overlay animates
// in from the tap point (~1s total), runs the callback mid-transition (the
// existing planner opens underneath), then fades out and unmounts.
//
// Brand colors reused exactly: #E11D48 (pink) → #A855F7 (purple).

const AITransitionContext = createContext(null);

export function useAITransition() {
  return useContext(AITransitionContext);
}

export function AITransitionProvider({ children }) {
  const [phase, setPhase] = useState(null); // null | { x, y, leaving }
  const lockRef = useRef(false);
  const location = useLocation();

  const play = useCallback((callback, origin) => {
    // Already on the planner (picker or open modal) → never replay the
    // transition over it, never re-navigate (would flash the open planner).
    if (location.pathname === "/planner") return;
    if (lockRef.current) return; // guard: no double-tap / duplicate navigation
    lockRef.current = true;
    try {
      navigator.vibrate?.(80); // silent fail on unsupported devices
    } catch { /* unsupported */ }
    const x = origin?.clientX ?? window.innerWidth / 2;
    const y = origin?.clientY ?? window.innerHeight / 2;
    setPhase({ x, y, leaving: false });
    // Planner navigation fires EARLY (t≈600ms) so the existing planner loads
    // underneath the overlay — AI generation is never delayed and the planner
    // is fully mounted long before the reveal. The overlay is a cinematic
    // cover, not a blocker.
    window.setTimeout(() => {
      try { callback?.(); } catch { /* never block the transition */ }
    }, 600);
    // Fade the overlay onto the ready planner at t≈3.45s; unmount at ≈3.85s.
    window.setTimeout(() => setPhase((p) => (p ? { ...p, leaving: true } : p)), 3450);
    window.setTimeout(() => { setPhase(null); lockRef.current = false; }, 3850);
  }, [location.pathname]);

  return (
    <AITransitionContext.Provider value={play}>
      {children}
      {phase && (
        <div
          className={`ai-transition-overlay font-display ${phase.leaving ? "ai-transition-leaving" : ""}`}
          style={{ "--tx": `${phase.x}px`, "--ty": `${phase.y}px` }}
          data-testid="ai-transition-overlay"
          role="presentation"
          aria-hidden="true"
        >
          <div className="ai-transition-trails" />
          <div className="ai-transition-particles" aria-hidden="true">
            {Array.from({ length: 9 }).map((_, i) => (
              <span key={i} className={`ai-particle ai-particle-${i + 1}`} />
            ))}
          </div>
          <div className="ai-transition-pulse" />
          <div className="ai-transition-orb" data-testid="ai-transition-orb">
            <span className="ai-orb-ring ai-orb-ring-1" />
            <span className="ai-orb-ring ai-orb-ring-2" />
            <span className="ai-orb-ring ai-orb-ring-3" />
            <Sparkles className="w-9 h-9 text-white" strokeWidth={2.25} />
          </div>
          <div className="ai-transition-greeting">
            <p className="ai-transition-text" data-testid="ai-transition-text">✨ AI Planning Mode</p>
            <p className="ai-transition-subtext" data-testid="ai-transition-subtext">Every celebration starts with a feeling.</p>
          </div>
        </div>
      )}
    </AITransitionContext.Provider>
  );
}
